import { toast } from 'vue-sonner';

/**
 * Subscribes the currently-authenticated user to their private Echo channel and surfaces
 * staff-assignment lifecycle events (CU-86d2dcc7y) as toasts + diary cache invalidations.
 *
 * Three notification types ride on the same channel:
 *
 * - `staff-assignment-pending` — fired by {@link App\\Notifications\\StaffAssignmentPendingNotification}
 *   when this user is freshly assigned to a task. Prompts them to accept or decline.
 *
 * - `staff-assignment-auto-accepted` — fired by
 *   {@link App\\Notifications\\StaffAssignmentAutoAcceptedNotification} when this user is freshly
 *   assigned but the company default auto-accepts the assignment. Nothing to action, but the
 *   diary must still refresh so the task appears without a manual reload.
 *
 * - `staff-assignment-status-changed` — fired by
 *   {@link App\\Notifications\\StaffAssignmentStatusChangedNotification} when someone else
 *   (an operator) accepts or declines on this user's behalf. Self-actions are intentionally
 *   silent server-side, so this user never gets a toast for an action they themselves took.
 *
 * Mounted at the diary-shell level — single subscription per CRM session. Tenant-wide ops
 * fan-out continues to flow through the existing `tenant.{tenantId}` `FixListUpdatedEvent`
 * pipe; this composable only handles the per-user surface.
 */

interface UseStaffAssignmentRealtimeOptions {
    /**
     * Pinia auth store exposing the current user. Must expose either `user.value.id` or
     * a `getUserId` getter — the composable reads via `userStore.user?.id` first, then
     * falls back to `userStore.getUserId` if that's the shape this tenancy uses.
     */
    userStore: any;
    /**
     * Pinia diary store. Used to invalidate the diaries TanStack Query cache on every
     * inbound assignment event so the diary refreshes within Pusher RTT.
     */
    diaryStore: any;
}

export function useStaffAssignmentRealtime(options: UseStaffAssignmentRealtimeOptions) {
    const { userStore, diaryStore } = options;

    const echoChannelName = ref<string | null>(null);

    const currentUserId = (): number | null => {
        const id = userStore?.user?.id ?? userStore?.getUserId ?? null;
        return id ? Number(id) : null;
    };

    // Notifications come in via the framework-broadcast event name; payload is the array
    // returned by `toBroadcast()` on the PHP side. We match on `payload.type` so a single
    // listener handles both notification classes.
    const broadcastEventName = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

    const handleInbound = (payload: any): void => {
        if (!payload || typeof payload !== 'object') {
            return;
        }

        const type = payload.type;
        const message = payload.message || 'Task assignment updated';

        if (type === 'staff-assignment-pending') {
            toast(message, {
                description: 'Open the diary to accept or decline.',
            });
        } else if (type === 'staff-assignment-auto-accepted') {
            // Company default is `accepted`: nothing for the assignee to action, but the diary
            // still needs to refresh so the new task shows up without a manual reload.
            toast(message);
        } else if (type === 'staff-assignment-status-changed') {
            if (payload.status === 'declined') {
                toast.error(message);
            } else {
                toast.success(message);
            }
        } else {
            // Unknown notification type — silently ignore (don't toast unrelated user
            // notifications that ride on the same channel).
            return;
        }

        // Refresh the diary so the avatar borders / fix-list pill / runsheet ticks
        // reflect the new state without a manual page reload.
        if (typeof diaryStore?.invalidateDiaries === 'function') {
            diaryStore.invalidateDiaries();
        }
    };

    const cleanupEchoListener = (): void => {
        if (echoChannelName.value && window.echo) {
            try {
                const channel = window.echo.private(echoChannelName.value);
                channel.stopListening(broadcastEventName);
            } catch (error) {
                console.error('Error stopping Echo listener in useStaffAssignmentRealtime:', error);
            }
        }
        echoChannelName.value = null;
    };

    const setupEchoListener = (): void => {
        const userId = currentUserId();

        if (!userId || !window.echo) {
            return;
        }

        const channelName = `App.Models.User.${userId}`;

        try {
            if (echoChannelName.value) {
                cleanupEchoListener();
            }

            const channel = window.echo.private(channelName);
            echoChannelName.value = channelName;

            channel
                .listen(broadcastEventName, (event: any) => {
                    handleInbound(event);
                })
                .error((error: any) => {
                    console.error('Echo channel error in useStaffAssignmentRealtime:', error);
                });
        } catch (error) {
            console.error('Failed to setup Echo listener in useStaffAssignmentRealtime:', error);
        }
    };

    watch(
        () => currentUserId(),
        (newId, oldId) => {
            if (newId !== oldId) {
                cleanupEchoListener();
                if (newId) {
                    setupEchoListener();
                }
            }
        },
        { immediate: true },
    );

    onUnmounted(() => {
        cleanupEchoListener();
    });

    return {
        cleanupEchoListener,
        setupEchoListener,
        // Exposed for Vitest — handleInbound is normally invoked by Echo, but tests can
        // call it directly with a mocked payload to assert the toast + invalidate path.
        handleInbound,
    };
}
